/**
 * 需纠偏底图（GCJ-02 等）下的 WGS84 栅格瓦片重采样（正射 2D 等）
 *
 * 业务栅格（无人机正射切片）是 WGS84 / Web Mercator；显示相机已是国测局坐标时，
 * 同一 z/x/y 的瓦片会整体偏几百米。与 gcjDemTiles 同思路：
 *   显示瓦片每个像素 → 当前底图适配器 fromDisplayFast 反算 WGS84 → 到原始瓦片取色。
 * 反算在 16px 网格上做、网格内双线性插值（GCJ 偏移在百米尺度近似平移，误差远小于 1 像素）。
 *
 * Mapbox GL 2.15 无 addProtocol：注册自定义地址 https://zhxt-raster.invalid/gcj/<key>/{z}/{x}/{y}.png，
 * 由 fetch 钩子拦截。与 DEM 钩子共存：本钩子挂在 window.__zhxtOrigFetch 一层，DEM 钩子的放行请求也会经过这里。
 */

import { getMapCoordAdapter, createCoordAdapter, DATUM_WGS84 } from './basemapCoord';
import { installGcjDemFetchHook } from './gcjDemTiles';

export const GCJ_RASTER_TILE_PREFIX = 'https://zhxt-raster.invalid/gcj';

const DEFAULT_TILE_SIZE = 256;
const GRID_STEP = 16;
const MAX_SRC_CACHE = 96;
const MAX_WARP_CACHE = 96;

/** key → 原始瓦片模板（含 {z}/{x}/{y}） */
const templates = new Map();
/** key → 绑定的底图 id（国界线等随样式构建的叠加层：按该底图纠偏，不依赖切换时序） */
const templateBasemaps = new Map();
const srcTileCache = new Map();
const warpTileCache = new Map();
let seq = 0;
let baseFetch = null;

function rememberLru(cache, key, value, max) {
  if (cache.has(key)) cache.delete(key);
  cache.set(key, value);
  while (cache.size > max) {
    const oldest = cache.keys().next().value;
    cache.delete(oldest);
  }
}

function lngToTileX(lng, z) {
  return ((lng + 180) / 360) * 2 ** z;
}

function latToTileY(lat, z) {
  const s = Math.sin((lat * Math.PI) / 180);
  const clamped = Math.min(Math.max(s, -0.9999), 0.9999);
  return (0.5 - Math.log((1 + clamped) / (1 - clamped)) / (4 * Math.PI)) * 2 ** z;
}

function tileXYToLngLat(x, y, z) {
  const n = 2 ** z;
  const lng = (x / n) * 360 - 180;
  const m = Math.PI - (2 * Math.PI * y) / n;
  const lat = (180 / Math.PI) * Math.atan(Math.sinh(m));
  return { lng, lat };
}

function fillTemplate(template, z, x, y) {
  return String(template)
    .replace(/\{z\}/g, String(z))
    .replace(/\{x\}/g, String(x))
    .replace(/\{y\}/g, String(y));
}

/**
 * 登记一个需要纠偏的栅格源，返回给 mapbox 的瓦片地址模板。
 * 同一原始模板复用同一 key（避免重复登记）。
 */
export function registerGcjRasterTemplate(tileTemplate, options = {}) {
  const tpl = String(tileTemplate || '');
  const boundId = options.basemapId ? String(options.basemapId) : null;
  for (const [k, v] of templates.entries()) {
    if (v === tpl && (templateBasemaps.get(k) || null) === boundId) {
      return `${GCJ_RASTER_TILE_PREFIX}/${k}/{z}/{x}/{y}.png`;
    }
  }
  seq += 1;
  const key = `s${seq}`;
  templates.set(key, tpl);
  if (boundId) templateBasemaps.set(key, boundId);
  return `${GCJ_RASTER_TILE_PREFIX}/${key}/{z}/{x}/{y}.png`;
}

/** 纠偏时使用的适配器：绑定了底图的模板按该底图，否则按当前全局底图（正射 2D，原行为） */
export function getWarpAdapterForKey(key) {
  const boundId = templateBasemaps.get(key);
  return boundId ? createCoordAdapter(boundId) : getMapCoordAdapter();
}

export function parseGcjRasterUrl(url) {
  const m = String(url || '').match(/zhxt-raster\.invalid\/gcj\/(s\d+)\/(\d+)\/(\d+)\/(\d+)\.png/i);
  if (!m) return null;
  return { key: m[1], z: Number(m[2]), x: Number(m[3]), y: Number(m[4]) };
}

async function decodeImage(arrayBuffer, type) {
  const blob = new Blob([arrayBuffer], { type: type || 'image/png' });
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(bitmap, 0, 0);
  const img = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
  if (typeof bitmap.close === 'function') bitmap.close();
  return { width: img.width, height: img.height, data: img.data };
}

async function encodePng(imageData) {
  const canvas = document.createElement('canvas');
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  canvas.getContext('2d').putImageData(imageData, 0, 0);
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('栅格 PNG 编码失败'))), 'image/png');
  });
  return blob.arrayBuffer();
}

function loadSrcTile(key, z, x, y, fetchFn) {
  const n = 2 ** z;
  if (y < 0 || y >= n) return Promise.resolve(null);
  const wx = ((x % n) + n) % n;
  const cacheKey = `${key}/${z}/${wx}/${y}`;
  if (srcTileCache.has(cacheKey)) return srcTileCache.get(cacheKey);
  const pending = (async () => {
    try {
      const res = await fetchFn(fillTemplate(templates.get(key), z, wx, y));
      if (!res || !res.ok) return null; // 范围外 404 → 透明
      const buf = await res.arrayBuffer();
      if (!buf || buf.byteLength === 0) return null;
      return await decodeImage(buf, res.headers?.get?.('Content-Type'));
    } catch (err) {
      srcTileCache.delete(cacheKey);
      return null;
    }
  })();
  rememberLru(srcTileCache, cacheKey, pending, MAX_SRC_CACHE);
  return pending;
}

function displayToWgsTileXY(adapter, z, fx, fy) {
  const { lng, lat } = tileXYToLngLat(fx, fy, z);
  const fn = adapter.fromDisplayFast || adapter.fromDisplay;
  const pair = fn(lng, lat);
  return [lngToTileX(pair[0], z), latToTileY(pair[1], z)];
}

/**
 * 计算显示瓦片（z,x,y）各像素对应的 WGS 瓦片坐标网格（纯函数，便于单测）
 * @returns {{ step:number, cols:number, grid:Float64Array }} grid[(r*cols+c)*2] = WGS 瓦片 x
 */
export function buildWarpGrid(adapter, z, x, y, size = DEFAULT_TILE_SIZE, step = GRID_STEP) {
  const cols = Math.ceil(size / step) + 1;
  const grid = new Float64Array(cols * cols * 2);
  for (let r = 0; r < cols; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const px = Math.min(c * step, size);
      const py = Math.min(r * step, size);
      const [wx, wy] = displayToWgsTileXY(adapter, z, x + px / size, y + py / size);
      grid[(r * cols + c) * 2] = wx;
      grid[(r * cols + c) * 2 + 1] = wy;
    }
  }
  return { step, cols, grid, size };
}

/** 网格内双线性插值：显示像素中心 (col+0.5,row+0.5) → WGS 瓦片坐标 */
export function sampleWarpGrid(g, col, row) {
  const u = (col + 0.5) / g.step;
  const v = (row + 0.5) / g.step;
  const c0 = Math.min(Math.floor(u), g.cols - 2);
  const r0 = Math.min(Math.floor(v), g.cols - 2);
  const fu = u - c0;
  const fv = v - r0;
  const at = (r, c, k) => g.grid[(r * g.cols + c) * 2 + k];
  const lerp = (k) => (
    at(r0, c0, k) * (1 - fu) * (1 - fv)
    + at(r0, c0 + 1, k) * fu * (1 - fv)
    + at(r0 + 1, c0, k) * (1 - fu) * fv
    + at(r0 + 1, c0 + 1, k) * fu * fv
  );
  return [lerp(0), lerp(1)];
}

async function warpRasterTile(key, z, x, y, fetchFn) {
  const adapter = getWarpAdapterForKey(key);
  const cacheKey = `${adapter.datum}/${key}/${z}/${x}/${y}`;
  if (warpTileCache.has(cacheKey)) return warpTileCache.get(cacheKey);

  const pending = (async () => {
    try {
      const size0 = DEFAULT_TILE_SIZE;
      const g0 = buildWarpGrid(adapter, z, x, y, size0);
      // 需要的原始瓦片：网格角点覆盖范围外扩 1
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (let i = 0; i < g0.grid.length; i += 2) {
        minX = Math.min(minX, g0.grid[i]);
        maxX = Math.max(maxX, g0.grid[i]);
        minY = Math.min(minY, g0.grid[i + 1]);
        maxY = Math.max(maxY, g0.grid[i + 1]);
      }
      const n = 2 ** z;
      const tiles = new Map();
      const jobs = [];
      for (let ty = Math.max(0, Math.floor(minY)); ty <= Math.min(n - 1, Math.floor(maxY)); ty += 1) {
        for (let tx = Math.floor(minX); tx <= Math.floor(maxX); tx += 1) {
          const wx = ((tx % n) + n) % n;
          jobs.push(loadSrcTile(key, z, wx, ty, fetchFn).then((t) => { if (t) tiles.set(`${wx}/${ty}`, t); }));
        }
      }
      await Promise.all(jobs);
      // 输出尺寸跟原始瓦片分辨率（256 或 512 高清）
      const first = tiles.values().next().value;
      const size = first ? first.width : size0;
      const g = size === size0 ? g0 : buildWarpGrid(adapter, z, x, y, size);
      const out = new ImageData(size, size);
      const px = out.data;
      if (tiles.size > 0) {
        for (let row = 0; row < size; row += 1) {
          for (let col = 0; col < size; col += 1) {
            const [fx, fy] = sampleWarpGrid(g, col, row);
            const tx = Math.floor(fx);
            const ty = Math.floor(fy);
            const t = tiles.get(`${((tx % n) + n) % n}/${ty}`);
            if (!t) continue;
            const sx = Math.min(t.width - 1, Math.max(0, Math.floor((fx - tx) * t.width)));
            const sy = Math.min(t.height - 1, Math.max(0, Math.floor((fy - ty) * t.height)));
            const si = (sy * t.width + sx) * 4;
            const di = (row * size + col) * 4;
            px[di] = t.data[si];
            px[di + 1] = t.data[si + 1];
            px[di + 2] = t.data[si + 2];
            px[di + 3] = t.data[si + 3];
          }
        }
      }
      return encodePng(out);
    } catch (err) {
      warpTileCache.delete(cacheKey);
      throw err;
    }
  })();
  rememberLru(warpTileCache, cacheKey, pending, MAX_WARP_CACHE);
  return pending;
}

function makeHook(base) {
  return function zhxtGcjRasterFetch(input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    const parsed = parseGcjRasterUrl(url);
    if (!parsed || !templates.has(parsed.key)) return base(input, init);
    return warpRasterTile(parsed.key, parsed.z, parsed.x, parsed.y, base)
      .then((buf) => new Response(buf, {
        status: 200,
        headers: { 'Content-Type': 'image/png' }
      }))
      .catch((err) => {
        console.warn('[ortho2d] GCJ 栅格纠偏失败', parsed, err?.message || err);
        return new Response(null, { status: 404 });
      });
  };
}

/**
 * 安装栅格纠偏 fetch 钩子（幂等）。
 * 挂在 window.__zhxtOrigFetch 一层：DEM 钩子已装时让它重新取一次下层，保证两者都生效。
 */
export function installGcjRasterFetchHook() {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  if (window.__zhxtGcjRasterFetchHooked) return;
  const native = window.__zhxtOrigFetch || window.fetch.bind(window);
  baseFetch = native;
  const hook = makeHook(baseFetch);
  window.__zhxtOrigFetch = hook;
  if (window.__zhxtGcjDemFetchHooked) {
    // DEM 钩子会读取新的 __zhxtOrigFetch 作为放行目标
    installGcjDemFetchHook();
  } else {
    window.fetch = hook;
  }
  window.__zhxtGcjRasterFetchHooked = true;
  console.info('[ortho2d] 已安装 GCJ 栅格纠偏 fetch 钩子');
}

/**
 * 按地图当前显示基准给出栅格源瓦片地址：WGS 底图原样返回；需纠偏底图返回纠偏地址。
 * @returns {{ tiles: string[], warped: boolean }}
 */
export function resolveRasterTilesForMap(map, tileTemplate) {
  const adapter = getMapCoordAdapter(map);
  if (!adapter.warpDisplay) return { tiles: [tileTemplate], warped: false };
  installGcjRasterFetchHook();
  return { tiles: [registerGcjRasterTemplate(tileTemplate)], warped: true };
}

/**
 * 按底图 id 给出 WGS84 栅格叠加层（如天地图国界线 ibo_w）的瓦片地址（构建样式时用，无需 map 实例）：
 * 该底图显示基准需纠偏时返回纠偏地址（每个子域名模板各登记一次），否则原样返回。
 * 数据本身非 WGS84 时不处理（不做二次转换）。
 * @param {string} basemapId
 * @param {string[]} tiles 已展开子域名的瓦片模板
 * @param {{ sourceDatum?: string }} [options]
 * @returns {{ tiles: string[], warped: boolean }}
 */
export function resolveRasterTilesForBasemap(basemapId, tiles, options = {}) {
  const list = Array.isArray(tiles) ? tiles : [];
  const sourceDatum = options.sourceDatum || DATUM_WGS84;
  if (!basemapId || !list.length || sourceDatum !== DATUM_WGS84) return { tiles: list, warped: false };
  const adapter = createCoordAdapter(basemapId);
  if (!adapter || !adapter.warpDisplay) return { tiles: list, warped: false };
  installGcjRasterFetchHook();
  return { tiles: list.map((t) => registerGcjRasterTemplate(t, { basemapId })), warped: true };
}
